import {AxiosInstance} from 'axios'

import {CaptureAPI} from '@/models/capture'

export type SubmitResult =
  | { kind: 'ok', enquiryId: number }
  | { kind: 'invalid', issuePaths: string[] }
  | { kind: 'verification_failed' }
  | { kind: 'rate_limited' }
  | { kind: 'upstream_timeout' }
  | { kind: 'upstream_error' }

interface EnquiryEnv {
  NODE_ENV?: string
  VUE_APP_API_BASE_URL?: string
  VUE_APP_ENQUIRY_URL?: string
}

// Production always submits (defaulting to the relay on the API domain). Other builds only submit when
// VUE_APP_ENQUIRY_URL is set, e.g. to a local dry-run relay; otherwise they log instead of posting.
export function resolveEnquiryUrl(env: EnquiryEnv): string | undefined {
  if (env.VUE_APP_ENQUIRY_URL) {
    return env.VUE_APP_ENQUIRY_URL
  }
  if (env.NODE_ENV === 'production') {
    return `${env.VUE_APP_API_BASE_URL || 'https://api.helicoptercharter.app/'}enquiries`
  }
  return undefined
}

// Only the field paths are read from a 400: they name what was rejected without carrying any customer values.
function extractIssuePaths(data: unknown): string[] {
  const issues = data && (data as { issues?: unknown }).issues
  if (!Array.isArray(issues)) {
    return []
  }
  return issues
    .map((issue) => issue && (issue as { path?: unknown }).path)
    .filter((path): path is string => typeof path === 'string')
}

export async function submitEnquiry(http: AxiosInstance, url: string, enquiry: CaptureAPI, turnstileToken: string): Promise<SubmitResult> {
  try {
    const response = await http.post(url, {enquiry, turnstileToken}, {timeout: 20000, validateStatus: () => true})
    switch (response.status) {
      case 201:
        return {kind: 'ok', enquiryId: response.data.enquiryId}
      case 400:
        return {kind: 'invalid', issuePaths: extractIssuePaths(response.data)}
      case 403:
        return {kind: 'verification_failed'}
      case 429:
        return {kind: 'rate_limited'}
      case 504:
        return {kind: 'upstream_timeout'}
      default:
        return {kind: 'upstream_error'}
    }
  } catch (error) {
    // axios reports its own client-side timeout as ECONNABORTED: the enquiry may still have arrived.
    return error && error.code === 'ECONNABORTED' ? {kind: 'upstream_timeout'} : {kind: 'upstream_error'}
  }
}
